import React from 'react';
import styled from '@emotion/styled';
import { EmotionBreakpoint } from 'dw-us-ui';
import {
	getImageBandItems,
	getImageEntrySources,
} from '../../utils/landingPageData';

// Full-width band of static image(s) uploaded to Contentful.
// Per spec:
//  - spans the full width of the page
//  - height is responsive to the uploaded image (natural aspect ratio)
//  - no padding above/below, so it buds up to the sections around it
//  - static image (JPG/PNG) with no URL/link
//  - mobile behaves the same way
const BandContainer = styled.div`
	display: block;
	width: 100%;
	margin: 0;
	padding: 0;
	line-height: 0; /* remove any inline gap below images */
	font-size: 0;

	.image-band__item {
		display: block;
		width: 100%;
		margin: 0;
		padding: 0;
	}

	picture,
	img {
		display: block;
		width: 100%;
		height: auto; /* responsive height based on the uploaded image */
	}
`;

// Spacer that offsets the fixed StickyBanner (position: fixed; top: 0) so the
// image band is not hidden underneath it when the band is the first section.
const BannerHeightAdjust = styled.div`
	height: 91px;
	${EmotionBreakpoint('mobile')} {
		height: 86px;
	}
`;

// Renders the `landingPage.imageBand` array (links to the existing `image`
// content type). Renders nothing when the field is absent or empty.
const ImageBand = ({ contentfulData, isFirstSection = false }) => {
	const items = getImageBandItems(contentfulData);
	if (!items.length) {
		return null;
	}

	const images = items
		.map((entry, index) => ({
			key: entry?.sys?.id || `image-band-${index}`,
			...getImageEntrySources(entry),
		}))
		.filter((image) => image.desktop);

	if (!images.length) {
		return null;
	}

	// Image band images are static (no URL), so they are never wrapped in a link.
	return (
		<>
			{isFirstSection && <BannerHeightAdjust />}
			<BandContainer className='image-band'>
				{images.map(({ key, desktop, mobile, alt }) => (
					<div key={key} className='image-band__item'>
						<picture>
							<source media='(max-width: 768px)' srcSet={mobile} />
							<source media='(min-width: 769px)' srcSet={desktop} />
							<img src={desktop} alt={alt} />
						</picture>
					</div>
				))}
			</BandContainer>
		</>
	);
};

export { ImageBand };
export default ImageBand;
